/**
 * Auth Store
 *
 * Handles authentication state and Yandex OAuth flow.
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getEventBus, platformEvents } from '@/core/mf-loader/EventBus'

// ============================================================================
// Types
// ============================================================================

export interface AuthUser {
  id: string
  email: string
  displayName: string
  avatar?: string
  orgSlug?: string
  roles?: string[]
}

interface AuthResponse {
  success: boolean
  token: string
  user: AuthUser
  expiresAt: string
}

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY = 'tlh:auth:token'
const USER_STORAGE_KEY = 'tlh:auth:user'

const YANDEX_CLIENT_ID = import.meta.env.VITE_YANDEX_CLIENT_ID || 'b97f08f6d3f14164bac8d299453b7714'
// Главный callback URL для OAuth (всегда www.tlh.ru в production)
const AUTH_CALLBACK_URL =
  import.meta.env.VITE_AUTH_CALLBACK_URL || `${window.location.origin}/auth/yandex/callback`
// API: в production используем прямой URL, в dev - прокси Vite
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/consultant'

/**
 * Build API URL based on environment
 * Production (API Gateway): /api/consultant/auth/yandex (path-based)
 * Development (Vite proxy): /api/consultant/auth/yandex (same format)
 */
function buildApiUrl(path: string): string {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE_URL}${normalizedPath}`
}

// ============================================================================
// Store
// ============================================================================

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref<string | null>(null)
  const user = ref<AuthUser | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Computed
  const isAuthenticated = computed(() => !!token.value && !!user.value)

  // Actions

  /**
   * Initialize from localStorage on app load
   */
  function init() {
    try {
      const storedToken = localStorage.getItem(STORAGE_KEY)
      const storedUser = localStorage.getItem(USER_STORAGE_KEY)

      if (storedToken && storedUser) {
        token.value = storedToken
        user.value = JSON.parse(storedUser)
        console.log('[Auth] Restored session for:', user.value?.email)
      }
    } catch (e) {
      console.error('[Auth] Failed to restore session:', e)
      clearStorage()
    }
  }

  /**
   * Generate state parameter for OAuth CSRF protection
   */
  function generateState(): string {
    const state = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    sessionStorage.setItem('tlh:auth:state', state)
    return state
  }

  /**
   * Verify state parameter from OAuth callback
   */
  function verifyState(state: string): boolean {
    const storedState = sessionStorage.getItem('tlh:auth:state')
    sessionStorage.removeItem('tlh:auth:state')
    return state === storedState
  }

  /**
   * Start Yandex OAuth flow
   */
  function loginWithYandex(orgSlug?: string) {
    const state = generateState()

    // Store orgSlug for callback
    if (orgSlug) {
      sessionStorage.setItem('tlh:auth:orgSlug', orgSlug)
    }

    // Store current origin for cross-domain redirect after auth
    const currentOrigin = window.location.origin
    sessionStorage.setItem('tlh:auth:originUrl', currentOrigin)

    // Store current path for redirect after auth
    const currentPath = window.location.pathname + window.location.search
    if (currentPath && currentPath !== '/auth/yandex/callback') {
      sessionStorage.setItem('tlh:auth:returnUrl', currentPath)
    }

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: YANDEX_CLIENT_ID,
      redirect_uri: AUTH_CALLBACK_URL,
      state,
    })

    // Redirect to Yandex OAuth
    window.location.href = `https://oauth.yandex.ru/authorize?${params.toString()}`
  }

  /**
   * Handle OAuth callback (exchange code for token)
   * Returns returnUrl to redirect to after successful auth
   * If original domain differs from current, returns crossDomainRedirect URL
   */
  async function handleCallback(
    code: string,
    state: string
  ): Promise<{ success: boolean; returnUrl?: string; crossDomainRedirect?: string }> {
    loading.value = true
    error.value = null

    try {
      // Verify state
      if (!verifyState(state)) {
        throw new Error('Invalid state parameter (CSRF protection)')
      }

      // Get orgSlug, returnUrl and originUrl from sessionStorage
      const orgSlug = sessionStorage.getItem('tlh:auth:orgSlug') || 'www.tlh.ru'
      const returnUrl = sessionStorage.getItem('tlh:auth:returnUrl') || '/'
      const originUrl = sessionStorage.getItem('tlh:auth:originUrl')
      sessionStorage.removeItem('tlh:auth:orgSlug')
      sessionStorage.removeItem('tlh:auth:returnUrl')
      sessionStorage.removeItem('tlh:auth:originUrl')

      // Exchange code for token
      const response = await fetch(buildApiUrl('/auth/yandex'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, orgSlug }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Authentication failed')
      }

      const data = (await response.json()) as AuthResponse

      if (!data.success || !data.token) {
        throw new Error('Invalid response from server')
      }

      // Check if we need cross-domain redirect
      const currentOrigin = window.location.origin
      if (originUrl && originUrl !== currentOrigin) {
        // Cross-domain: redirect to original domain with token in URL fragment
        // Fragment (#) is not sent to server, making it more secure
        console.log('[Auth] Cross-domain redirect:', currentOrigin, '→', originUrl)
        const redirectUrl = `${originUrl}/auth/callback#token=${encodeURIComponent(data.token)}&user=${encodeURIComponent(JSON.stringify(data.user))}&returnUrl=${encodeURIComponent(returnUrl)}`
        return { success: true, crossDomainRedirect: redirectUrl }
      }

      // Same domain: store credentials locally
      token.value = data.token
      user.value = data.user
      saveToStorage()

      // Notify MF about login via EventBus
      const bus = getEventBus()
      platformEvents.userUpdated(bus, {
        id: data.user.id,
        displayName: data.user.displayName,
        email: data.user.email,
        avatar: data.user.avatar || null,
        roles: data.user.roles || [],
        orgId: null,
        orgSlug: data.user.orgSlug || null,
        isAuthenticated: true,
      })

      console.log('[Auth] Logged in as:', data.user.email)
      return { success: true, returnUrl }
    } catch (e) {
      console.error('[Auth] Callback error:', e)
      error.value = e instanceof Error ? e.message : 'Authentication failed'
      return { success: false }
    } finally {
      loading.value = false
    }
  }

  /**
   * Handle cross-domain callback (receive token from URL fragment)
   * Called on the original domain after redirect from main OAuth domain
   */
  function handleCrossDomainCallback(): { success: boolean; returnUrl?: string } {
    try {
      const hash = window.location.hash.slice(1) // Remove #
      if (!hash) {
        return { success: false }
      }

      const params = new URLSearchParams(hash)
      const tokenValue = params.get('token')
      const userJson = params.get('user')
      const returnUrl = params.get('returnUrl') || '/'

      if (!tokenValue || !userJson) {
        console.error('[Auth] Missing token or user in cross-domain callback')
        return { success: false }
      }

      // Parse and store credentials
      const userData = JSON.parse(decodeURIComponent(userJson)) as AuthUser
      token.value = decodeURIComponent(tokenValue)
      user.value = userData
      saveToStorage()

      // Clear URL fragment
      window.history.replaceState(null, '', window.location.pathname + window.location.search)

      // Notify MF about login via EventBus
      const bus = getEventBus()
      platformEvents.userUpdated(bus, {
        id: userData.id,
        displayName: userData.displayName,
        email: userData.email,
        avatar: userData.avatar || null,
        roles: userData.roles || [],
        orgId: null,
        orgSlug: userData.orgSlug || null,
        isAuthenticated: true,
      })

      console.log('[Auth] Cross-domain login as:', userData.email)
      return { success: true, returnUrl: decodeURIComponent(returnUrl) }
    } catch (e) {
      console.error('[Auth] Cross-domain callback error:', e)
      return { success: false }
    }
  }

  /**
   * Fetch current user from API (verify token)
   */
  async function fetchCurrentUser(): Promise<boolean> {
    if (!token.value) return false

    loading.value = true
    error.value = null

    try {
      const response = await fetch(buildApiUrl('/auth/me'), {
        headers: {
          Authorization: `Bearer ${token.value}`,
        },
      })

      if (!response.ok) {
        if (response.status === 401) {
          // Token expired or invalid
          logout()
          return false
        }
        throw new Error('Failed to fetch user')
      }

      const data = await response.json()
      user.value = data.user
      saveToStorage()
      return true
    } catch (e) {
      console.error('[Auth] Fetch user error:', e)
      error.value = e instanceof Error ? e.message : 'Failed to fetch user'
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Logout
   */
  function logout() {
    token.value = null
    user.value = null
    clearStorage()

    // Notify MF about logout via EventBus
    const bus = getEventBus()
    platformEvents.userUpdated(bus, {
      id: null,
      displayName: null,
      email: null,
      avatar: null,
      roles: [],
      orgId: null,
      orgSlug: null,
      isAuthenticated: false,
    })

    console.log('[Auth] Logged out')
  }

  /**
   * Get Authorization header value
   */
  function getAuthHeader(): string | null {
    return token.value ? `Bearer ${token.value}` : null
  }

  // Storage helpers

  function saveToStorage() {
    try {
      if (token.value) {
        localStorage.setItem(STORAGE_KEY, token.value)
      }
      if (user.value) {
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user.value))
      }
    } catch (e) {
      console.warn('[Auth] Failed to save to localStorage (quota exceeded?):', e)
    }
  }

  function clearStorage() {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(USER_STORAGE_KEY)
  }

  return {
    // State
    token,
    user,
    loading,
    error,
    // Computed
    isAuthenticated,
    // Actions
    init,
    loginWithYandex,
    handleCallback,
    handleCrossDomainCallback,
    fetchCurrentUser,
    logout,
    getAuthHeader,
  }
})
